import { useEffect, useMemo, useState } from 'react';
import api from '../../../../components/utils/api';
import Spinner from '../../../../components/template/Spinner';
import MainTitle from '../../../../components/template/MainTitle';
import ModelGraph from './ModelGraph';

// Display order: end-to-end first, then partial pipelines.
const VARIANT_ORDER = ['model_all', 'model_main', 'model_pre'];

const formatShape = (shape) => {
    if (!shape || shape.length === 0) return <span className='text-gray-400'>unknown</span>;
    return `[${shape.map(d => d == null ? '?' : d).join(', ')}]`;
};

// Stats come back null when the tensor holds NaN/Inf values.
const formatStat = (value) => (value == null ? '—' : Number(value).toPrecision(4));

// A tensor is flagged when its |min|/|max| magnitude dwarfs the median
// magnitude across tensors — such a range forces INT8 quantization to crush
// the typical weights into a few integer steps.
const OUTLIER_RATIO = 6;
const OUTLIER_TOOLTIP =
    `Heuristic: this tensor's min-max magnitude exceeds ${OUTLIER_RATIO}x the median across tensors. ` +
    'Such a wide range degrades INT8 quantization for this layer.';

const TensorRow = ({ spec }) => (
    <tr className='border-t'>
        <td className='py-2 px-3 font-mono text-xs break-all max-w-xs'>{spec.name}</td>
        <td className='py-2 px-3 font-mono text-xs'>{formatShape(spec.shape)}</td>
        <td className='py-2 px-3 font-mono text-xs'>{spec.dtype}</td>
        <td className='py-2 px-3 font-mono text-xs'>
            {spec.quantization
                ? `scale=${spec.quantization.scale}, zp=${spec.quantization.zero_point}`
                : '—'}
        </td>
    </tr>
);

const TensorTable = ({ specs }) => {
    if (!specs || specs.length === 0) {
        return <div className='text-sm text-gray-500'>No tensors</div>;
    }
    return (
        <table className='w-full text-left text-sm'>
            <thead className='bg-gray-50'>
                <tr>
                    <th className='py-2 px-3'>Name</th>
                    <th className='py-2 px-3'>Shape</th>
                    <th className='py-2 px-3'>Dtype</th>
                    <th className='py-2 px-3'>Quantization</th>
                </tr>
            </thead>
            <tbody>
                {specs.map((spec, i) => <TensorRow key={i} spec={spec} />)}
            </tbody>
        </table>
    );
};

const WeightStatsSection = ({ weightStats }) => {
    // Collapsed by default — the table can run to hundreds of rows. The
    // header summary (tensor/outlier counts) stays visible either way.
    const [expanded, setExpanded] = useState(false);
    const tensors = useMemo(() => weightStats?.tensors ?? [], [weightStats]);

    // Median-based so a single bad tensor can't inflate the baseline and
    // hide itself.
    const outlierFlags = useMemo(() => {
        const magnitudes = tensors.map(t => Math.max(Math.abs(t.min ?? 0), Math.abs(t.max ?? 0)));
        const sorted = [...magnitudes].sort((a, b) => a - b);
        const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;
        return magnitudes.map(m => median > 0 && m > OUTLIER_RATIO * median);
    }, [tensors]);

    if (tensors.length === 0) {
        return (
            <section className='my-4'>
                <h3 className='font-bold text-base mb-2'>Weights</h3>
                <div className='text-sm text-gray-500'>Weight statistics not available for this variant.</div>
            </section>
        );
    }

    const outlierCount = outlierFlags.filter(Boolean).length;
    const hasDequantized = tensors.some(t => t.dequantized);
    return (
        <section className='my-4'>
            <h3 className='font-bold text-base mb-2'>
                <button
                    type='button'
                    onClick={() => setExpanded(v => !v)}
                    className='inline-flex items-center gap-1 hover:text-violet-500'
                >
                    <span className='text-xs'>{expanded ? '▾' : '▸'}</span>
                    Weights
                </button>
                <span className='ml-2 text-xs font-normal text-gray-500'>
                    {weightStats?.truncated
                        ? `${tensors.length} largest-magnitude tensors shown (rest omitted)`
                        : `${tensors.length} tensors`}
                </span>
                {outlierCount > 0 && (
                    <span
                        className='ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-normal'
                        title={OUTLIER_TOOLTIP}
                    >
                        {outlierCount} possible outlier{outlierCount > 1 ? 's' : ''}
                    </span>
                )}
            </h3>
            {expanded && (
                <>
                    <table className='w-full text-left text-sm'>
                        <thead className='bg-gray-50'>
                            <tr>
                                <th className='py-2 px-3'>Name</th>
                                <th className='py-2 px-3'>Shape</th>
                                <th className='py-2 px-3'>Dtype</th>
                                <th className='py-2 px-3'>Min</th>
                                <th className='py-2 px-3'>Max</th>
                                <th className='py-2 px-3'>Mean</th>
                                <th className='py-2 px-3'>Std</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tensors.map((tensor, i) => (
                                <tr key={i} className={`border-t ${outlierFlags[i] ? 'bg-red-50' : ''}`}>
                                    <td className='py-2 px-3 font-mono text-xs break-all max-w-xs'>
                                        {tensor.name}
                                        {outlierFlags[i] && (
                                            <span
                                                className='ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-sans font-medium'
                                                title={OUTLIER_TOOLTIP}
                                            >
                                                possible outlier
                                            </span>
                                        )}
                                    </td>
                                    <td className='py-2 px-3 font-mono text-xs'>{formatShape(tensor.shape)}</td>
                                    <td className='py-2 px-3 font-mono text-xs'>{tensor.dtype}</td>
                                    <td className='py-2 px-3 font-mono text-xs'>{formatStat(tensor.min)}</td>
                                    <td className='py-2 px-3 font-mono text-xs'>{formatStat(tensor.max)}</td>
                                    <td className='py-2 px-3 font-mono text-xs'>{formatStat(tensor.mean)}</td>
                                    <td className='py-2 px-3 font-mono text-xs'>{formatStat(tensor.std)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {hasDequantized && (
                        <div className='mt-1 text-xs text-gray-500'>
                            Integer weights are shown dequantized (scale/zero-point applied), so their
                            range is comparable to the float source weights.
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

const VariantSections = ({ payload }) => (
    <>
        <section className='my-4'>
            <h3 className='font-bold text-base mb-2'>Inputs</h3>
            <TensorTable specs={payload?.input_specs} />
        </section>

        <section className='my-4'>
            <h3 className='font-bold text-base mb-2'>Outputs</h3>
            <TensorTable specs={payload?.output_specs} />
        </section>

        <WeightStatsSection weightStats={payload?.weight_stats} />

        <section className='my-4'>
            <h3 className='font-bold text-base mb-2'>
                Layer Graph
                {payload?.graph_json?.truncated && (
                    <span className='ml-2 text-xs font-normal text-gray-500'>
                        (truncated to first 500 nodes)
                    </span>
                )}
            </h3>
            {payload?.graph_json
                ? <ModelGraph graph={payload.graph_json} />
                : <div className='text-sm text-gray-500'>Graph view not available for this variant.</div>
            }
        </section>
    </>
);

const ModelInfoModal = ({ convertedModelId, framework, dataType }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeVariant, setActiveVariant] = useState(null);

    useEffect(() => {
        if (!convertedModelId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setActiveVariant(null);
        api.get(`/api/model-information/converted-models/${convertedModelId}/specs`)
            .then(res => {
                if (cancelled) return;
                setData(res.data);
                if (res.data?.error) setError(res.data.error);
            })
            .catch(err => {
                if (cancelled) return;
                console.error(err);
                setError('Failed to fetch model specs');
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [convertedModelId]);

    const orderedVariants = useMemo(() => {
        const variants = data?.variants ?? {};
        return VARIANT_ORDER.filter(v => variants[v]);
    }, [data]);

    // Default to the first available variant once data lands.
    useEffect(() => {
        if (orderedVariants.length > 0 && activeVariant == null) {
            setActiveVariant(orderedVariants[0]);
        }
    }, [orderedVariants, activeVariant]);

    const showSections = !loading && data && orderedVariants.length > 0;
    const selectedPayload = data?.variants?.[activeVariant];

    return (
        <div>
            <MainTitle title={`${framework} · ${dataType}`} />

            {loading && (
                <div className='flex items-center gap-2 my-4 text-gray-500'>
                    <Spinner /> Loading model specs (may take several seconds for legacy models)…
                </div>
            )}

            {error && (
                <div className='my-3 p-3 bg-gray-50 border border-gray-200 text-sm text-gray-700 rounded whitespace-pre-line'>
                    {error}
                </div>
            )}

            {showSections && (
                <>
                    {orderedVariants.length > 1 && (
                        <div className='flex gap-2 border-b border-gray-200 mt-4'>
                            {orderedVariants.map(variant => {
                                const active = variant === activeVariant;
                                return (
                                    <button
                                        key={variant}
                                        type='button'
                                        onClick={() => setActiveVariant(variant)}
                                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${active
                                            ? 'text-violet-500 border-violet-500'
                                            : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                                    >
                                        {variant}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <VariantSections payload={selectedPayload} />
                </>
            )}
        </div>
    );
};

export default ModelInfoModal;
